<svelte:options
  customElement={{
    tag: "oc-radio-button-v1",

    extend: window.__components.extend({
      formAssociated: true,
      inputBehavior: true,
    }),
    props: {
      name: { type: "String", reflect: true }, /*                                    */
      labelPlacement: { type: "String", attribute: "label-placement" },
      value: { type: "String" },
      checked: { type: "Boolean" }, /*                                     */
      variant: { type: "String" },
      fitContent: { type: "Boolean", attribute: "fit-content" },
      disabled: { type: "Boolean", reflect: true }, /*                                    */
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      ocAriaRequired: { type: "Boolean", attribute: "oc-aria-required" },
    },
  }}
/>

<script lang="ts">
  import { exposeStatesForCSS } from "@otto-ec/otto-components-utils/utils";
  import {
    ariaDescriptionWithValidation,
    implicitSubmit,
    refireNonComposableNativeEvent,
    singleSelection,
    stopLabelClickPropagation,
  } from "../../../common/actions";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import { FormLabelIconV1 } from "../../../common/components/FormLabelIcon";
  import { useSlots } from "../../../common/utils/useSlots.svelte";

  import type { Props } from "./RadioButtonV1.types";

  let {
    name = undefined,
    labelPlacement = undefined,
    value = "on",
    checked = false,
    variant = "default",
    fitContent = false,
    disabled = false,
    ocAriaLabel = undefined,
    ocAriaRequired = false,
    hint = undefined,
    validationMessage = undefined,
    internals,
  }: Props & {
    internals: ElementInternals;
    hint?: string;
    validationMessage?: string;
  } = $props();

  const Host = $host();

  const slots = useSlots(Host);

  export function resetForm(): void {
    /*                            */
    checked = Host.hasAttribute("checked");
  }

  let computedVariant = $derived(validationMessage ? "error" : variant);

  $effect(() => {
    internals.setFormValue(checked ? value : null);
  });

  const onChange = (event: Event): void => {
    const input = event.target as HTMLInputElement;
    if (!event.defaultPrevented) checked = input.checked;
  };

  $effect(() => {
    exposeStatesForCSS(Host, { checked });
  });
</script>

<label
  class="radio"
  class:radio--fit-content={fitContent}
  class:radio--error={computedVariant === "error"}
  use:stopLabelClickPropagation
>
  <!-- Left Label Placement -->
  {#if slots.default && labelPlacement === "left"}
    <span class="radio__label" id="label" aria-hidden="true">
      <slot />
      <FormLabelIconV1 variant={computedVariant} />
    </span>
  {/if}

  <span>
    <!-- svelte-ignore a11y_role_supports_aria_props_implicit-->
    <input
      type="radio"
      class="radio__input"
      {checked}
      {name}
      {disabled}
      use:singleSelection={{ Host, name, checked }}
      use:refireNonComposableNativeEvent={Host}
      use:implicitSubmit={internals}
      use:ariaDescriptionWithValidation={{ validationMessage, hint }}
      onchange={onChange}
      aria-labelledby={ocAriaLabel ? undefined : "label"}
      aria-label={ocAriaLabel}
      aria-required={ocAriaRequired}
    />
    <!-- The box displays the outline and the icon. This is needed because input element doesn't support pseudo elements. -->
    <span class="radio__circle"></span>
  </span>

  <!-- Right Label Placement -->
  {#if slots.default && labelPlacement !== "left"}
    <span class="radio__label" id="label" aria-hidden="true">
      <slot />
      <FormLabelIconV1 variant={computedVariant} />
    </span>
  {/if}
</label>

<DefaultStyles />

<style lang="scss">
  @use "@otto-ec/otto-components-utils/scss/mixins";
  @use "@otto-ec/design-tokens/component" as tokens;
  :global {
    :host {
      display: block;
    }

    .radio {
      display: flex;
      align-items: flex-start;
      /*                                                 */
      /*                                       */
      gap: tokens.$oc-semantic-spacing-75;

      color: tokens.$oc-component-form-default-label-color;
      font: tokens.$oc-component-form-field-input-font;

      &--fit-content {
        width: fit-content;
      }
    }

    .radio__input {
      all: unset;
      position: absolute;
      /*                                                            */
      /*                                       */
      width: tokens.$oc-base-dimension-relative-24;
      /*                                                             */
      /*                                       */
      height: tokens.$oc-base-dimension-relative-24;

      /*                 */
      --oc-floating-focus-v1-radius: 50%;
    }

    .radio__label {
      flex: 1;
      min-width: 0;
    }

    .radio__circle {
      position: relative;
      display: block;
      /*                                                            */
      /*                                                             */
      /*                                       */
      width: tokens.$oc-base-dimension-relative-24;
      height: tokens.$oc-base-dimension-relative-24;

      outline: 1px solid tokens.$oc-component-form-default-border-color;
      border-radius: 50%;

      background-color: tokens.$oc-component-form-background-color;
    }

    /*                   */
    .radio__input:checked ~ .radio__circle {
      background-color: tokens.$oc-component-form-background-color-checked;
      &:before {
        $center-dot: 7px;

        content: "";
        display: block;
        position: absolute;
        left: $center-dot;
        top: $center-dot;
        bottom: $center-dot;
        right: $center-dot;

        border-radius: 50%;
        background-color: tokens.$oc-component-form-default-icon-color-checked;
      }
    }

    /*                 */
    .radio--error {
      color: tokens.$oc-component-form-error-label-color;

      .radio__circle {
        outline-color: tokens.$oc-component-form-error-border-color;
      }

      .radio__input:checked ~ .radio__circle {
        background-color: tokens.$oc-component-form-error-background-color-checked;
        box-shadow: 0 0 0 1px tokens.$oc-component-form-error-background-color-checked;
      }
    }

    /*                 */
    @media (hover: hover) {
      :host(:enabled) {
        .radio:hover {
          cursor: pointer;

          .radio__circle {
            outline-width: tokens.$oc-semantic-focus-outline-offset;
          }
        }
      }
    }

    /*                  */
    :host(:enabled) {
      .radio__input:active ~ .radio__circle {
        outline-width: tokens.$oc-semantic-focus-outline-offset;
      }

      .radio:not(.radio--error) .radio__input:active ~ .radio__circle {
        outline-color: tokens.$oc-component-form-default-border-color-active;
      }
    }

    /*                    */
    :host(:disabled) {
      .radio {
        color: tokens.$oc-component-form-disabled-label-color;
      }

      .radio__circle {
        outline-color: tokens.$oc-component-form-disabled-border-color;
        background-color: tokens.$oc-component-form-disabled-background-color;

        &:before {
          background: tokens.$oc-component-form-disabled-icon-color;
        }
      }
    }
  }
</style>
